import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowRight, Crown, Target, TrendingUp, Sparkles, Users2, Users, Workflow, LineChart, Megaphone, CheckCircle2, Rocket, Brain, Zap, FileText, Globe } from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ScoreGauge } from "@/components/ScoreGauge";
import { LevelBadge } from "@/components/LevelBadge";
import { LeadForm, LeadFormData } from "@/components/LeadForm";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { useWebsiteScan } from "@/hooks/use-website-scan";
import { DownloadReportEmailForm } from "@/components/DownloadReportEmailForm";
import { ReportFooter } from "@/components/ReportFooter";
import { QuizAILoader } from "@/components/QuizAILoader";
import { AIInsightCard } from "@/components/AIInsightCard";
import { Skeleton } from "@/components/ui/skeleton";
import { useReportTracking } from "@/hooks/use-report-tracking";

const ReportHigh = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState<any>(null);
  const [isLoadingAI, setIsLoadingAI] = useState(true);
  
  const { score, answers } = location.state || {};
  const band = 'high';
  const reportToken = (location.state as any)?.reportToken;
  const quizSubmissionId = (location.state as any)?.quizSubmissionId;
  const leadSubmissionId = (location.state as any)?.leadSubmissionId;
  
  // Track report view
  useReportTracking({
    reportToken,
    quizSubmissionId,
    leadSubmissionId
  });
  
  const { logoUrl } = useWebsiteScan({
    initialScan: (location.state as any)?.websiteScan,
    domain: (answers?.[4] as string) || undefined,
    quizSubmissionId: quizSubmissionId || undefined,
  });
  
  // Helper function to format domain for display
  const formatDomainForDisplay = (domain: string): string => {
    if (!domain) return '';
    return domain
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .trim();
  };
  
  // Track report view
  useEffect(() => {
    if (typeof window !== 'undefined' && window.fbq) {
      // Custom event
      window.fbq('trackCustom', 'ReportViewed', {
        report_type: band,
        score: score,
        timestamp: new Date().toISOString()
      });
      
      // Standard ViewContent event
      window.fbq('track', 'ViewContent', {
        content_name: 'LinkedIn Readiness Report - High',
        content_category: 'report',
        content_type: band,
        value: score
      });
    }
  }, [band, score]);
  
  // Load AI analysis from database (already generated during quiz completion)
  useEffect(() => {
    const loadAnalysis = async () => {
      try {
        setIsLoadingAI(true);
        const quizSubmissionId = (location.state as any)?.quizSubmissionId;
        
        if (quizSubmissionId) {
          logger.debug('Loading AI analysis from database for quiz:', quizSubmissionId);
          
          // Try to fetch from database first
          const { data: quizData, error: dbError } = await supabase
            .from('quiz_submissions')
            .select('ai_analysis')
            .eq('id', quizSubmissionId)
            .single();
          
          if (dbError) {
            console.error('Error loading AI analysis from database:', dbError);
          } else if (quizData?.ai_analysis) {
            logger.debug('AI analysis loaded from database');
            setAiAnalysis(quizData.ai_analysis);
            setIsLoadingAI(false);
            return;
          }
        }
        
        // Fallback: Generate if not in database (legacy quizzes)
        logger.debug('No AI analysis in database, generating as fallback');
        const quizData = {
          answers: {
            ...(answers || {}),
            found_linkedin_profile: (location.state as any)?.foundLinkedInProfile
          },
          websiteScan: (location.state as any)?.websiteScan || {},
          competitorAnalysis: (location.state as any)?.competitorAnalysis || {},
          interstitialAnswers: (location.state as any)?.interstitialAnswers || {}
        };

        const { data, error } = await supabase.functions.invoke('generate-quiz-recommendations', {
          body: { quizData, band: 'high', quizSubmissionId }
        });

        if (error) {
          console.error('Error generating AI analysis:', error);
          setAiAnalysis(null);
        } else if (data?.analysis) {
          setAiAnalysis(data.analysis);
          console.log('✅ AI analysis generated and saved by edge function');
        }
      } catch (error) {
        console.error('Failed to load AI analysis:', error);
        setAiAnalysis(null);
      } finally {
        setIsLoadingAI(false);
      }
    };

    if (answers && Object.keys(answers).length > 0) {
      loadAnalysis();
    } else {
      setIsLoadingAI(false);
    }
  }, [answers, band, location.state]);
  
  const handleSubmit = async (data: LeadFormData) => {
    setIsSubmitting(true);
    
    try {
      logger.debug('Submitting lead via edge function');
      
      const quizSubmissionId = (location.state as any)?.quizSubmissionId || null;
      
      const { data: result, error } = await supabase.functions.invoke('submit-lead', {
        body: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          company: null,
          position: null,
          quizScore: score,
          quizBand: band,
          quizAnswers: answers,
          quizSubmissionId: quizSubmissionId
        }
      });

      if (error) {
        logger.error('Submission error', error);
        throw error;
      }

      if (!result.success) {
        throw new Error(result.error || 'Submission failed');
      }

      logger.info('Lead submission successful');
      
      // Track headline conversion with lead_submission_id
      const sessionId = localStorage.getItem('headline_session_id');
      const variantId = localStorage.getItem('assigned_variant_id');
      
      if (sessionId && variantId && result.leadId) {
        try {
          await supabase.from('headline_conversions').insert({
            variant_id: variantId,
            session_id: sessionId,
            lead_submission_id: result.leadId,
            quiz_submission_id: quizSubmissionId || null
          });
          logger.info('✅ Lead conversion tracked for A/B test');
        } catch (convError) {
          logger.error('❌ Failed to track conversion', convError);
        }
      }
      
      // Track lead form submission
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('trackCustom', 'LeadFormSubmitted', {
          report_band: band,
          score: score,
          form_type: 'Report Lead Form'
        });
        
        // Also track as standard Lead event
        window.fbq('track', 'Lead', {
          content_name: 'LinkedIn Readiness Report',
          content_category: band,
          value: score
        });
      }
      
      // Push lead form submission to GTM dataLayer
      if (typeof window !== 'undefined' && (window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: 'lead_form_submitted',
          lead_id: result.leadId,
          report_band: band,
          quiz_score: score,
          email: data.email,
          event_category: 'lead_generation',
          event_label: `Band: ${band} | Score: ${score}`
        });
        console.log('✅ GTM lead form submission event pushed');
      }
      
      toast.success("Dziękujemy! Twój raport został zapisany.");
      setIsSubmitting(false);
      setIsDialogOpen(false);
      
      setTimeout(() => {
        navigate("/dziekujemy");
      }, 500);
    } catch (error) {
      logger.error('Error saving lead', error);
      toast.error("Wystąpił błąd. Spróbuj ponownie.");
      setIsSubmitting(false);
    }
  };
  
  const quickWins = [
    { id: "1", label: "ABM program dla top 50 kont strategicznych" },
    { id: "2", label: "Thought leadership campaign (reklamy + PR)" },
    { id: "3", label: "AI-powered lead scoring i predictive analytics" },
    { id: "4", label: "Multi-channel orchestration (LinkedIn + email + ads)" },
    { id: "5", label: "Advanced attribution modeling i ROI dashboard" },
    { id: "6", label: "Executive thought leadership program" },
    { id: "7", label: "Competitive intelligence przez LinkedIn" }
  ];
  
  const recommendations = [
    {
      icon: <Target className="h-10 w-10 text-primary" />,
      title: "Account-Based Marketing (ABM) na sterydach",
      description: "Hiperpersonalizowana strategia dla top 50-100 kont strategicznych. Koordynowane działania całego zespołu z pełną integracją danych.",
      howTo: [
        "Segmentacja: tier 1 (20 kont), tier 2 (50 kont), tier 3 (100+ kont)",
        "Dla tier 1: dedykowane kampanie, spersonalizowane treści, direct outreach",
        "Multi-threading: kontakt z 3-5 osobami w każdej firmie docelowej",
        "Account insights: LinkedIn + ZoomInfo + 6sense + intent data",
        "Koordynacja sales + marketing: tygodniowe syncs, wspólne cele",
        "Personalizowane landing pages i content dla każdego top konta"
      ]
    },
    {
      icon: <Brain className="h-10 w-10 text-primary" />,
      title: "AI & Predictive Analytics w lead generation",
      description: "Wykorzystaj sztuczną inteligencję do przewidywania konwersji, automatyzacji i optymalizacji każdego touchpoint.",
      howTo: [
        "Lead scoring AI: przewiduj conversion probability na bazie danych historycznych",
        "Intent data + predictive signals: kto jest ready to buy",
        "Automated enrichment: auto-douszczegółowienie leadów (stanowisko, tech stack, budżet)",
        "AI content recommendations: co pokazać komu i kiedy",
        "Chatboty i conversational AI na landing pages",
        "Dynamic content personalization w czasie rzeczywistym"
      ]
    },
    {
      icon: <Crown className="h-10 w-10 text-primary" />,
      title: "Executive Thought Leadership na skalę",
      description: "CEO i kluczowi liderzy jako influencerzy branżowi. Personal branding wspierany budżetem reklamowym i PR.",
      howTo: [
        "Program ghostwritingu dla 3-5 kluczowych ekspertów",
        "Posty CEO: 2-3x/tyg, wsparcie reklamowe 2-5k PLN/post",
        "LinkedIn Live i Audio Events z CEO raz w miesiącu",
        "Artykuły zewnętrzne (media branżowe) promowane na LinkedIn",
        "Speaking opportunities: konferencje, webinary, podcasty",
        "Budowanie community: grupa/newsletter LinkedIn dla ekspertów"
      ]
    },
    {
      icon: <Workflow className="h-10 w-10 text-primary" />,
      title: "Multi-Channel Orchestration",
      description: "Zsynchronizowane kampanie: LinkedIn + email + retargeting + direct mail. Każdy touchpoint ma znaczenie.",
      howTo: [
        "Sekwencje wielokanałowe: LinkedIn touch → email → ad retarget → sales call",
        "Retargeting LinkedIn Ads dla visitors z website i content downloads",
        "Email nurturing zintegrowany z LinkedIn activity (otworzył post? → dostaje email)",
        "Direct mail dla top 20 kont (yes, fizyczne listy/prezenty!)",
        "SMS/WhatsApp dla ciepłych leadów (za zgodą)",
        "Unified dashboard: wszystkie kanały w jednym miejscu"
      ]
    },
    {
      icon: <LineChart className="h-10 w-10 text-primary" />,
      title: "Advanced Analytics & Attribution",
      description: "Pełna widoczność ROI, multi-touch attribution, predykcje revenue. Data-driven decision making na każdym kroku.",
      howTo: [
        "Multi-touch attribution model (first, last, linear, time-decay)",
        "Revenue attribution per channel, campaign, content asset",
        "Pipeline velocity metrics: jak szybko leady przechodzą przez lejek",
        "Predictive revenue forecasting na bazie pipeline",
        "Cohort analysis: które kampanie dają najlepszy LTV",
        "Executive dashboard: real-time ROI, pipeline, conversion rates"
      ]
    },
    {
      icon: <Megaphone className="h-10 w-10 text-primary" />,
      title: "Paid Social + Organic Synergy",
      description: "Zaawansowane kampanie reklamowe LinkedIn wspierające organic reach. Budget 10-50k PLN/mies.",
      howTo: [
        "Thought leadership ads: promuj najlepsze organic posty ekspertów",
        "Lead gen forms: gated content (raporty, webinary, konsultacje)",
        "Retargeting audiences: website visitors, video viewers, post engagers",
        "Lookalike audiences na bazie best customers",
        "A/B testing: kreacje, copy, CTA, landing pages",
        "Constant optimization: weekly adjustments based on performance"
      ]
    },
    {
      icon: <Target className="h-10 w-10 text-primary" />,
      title: "Targetowana precyzyjna reklama na konkretne osoby (Matched Audiences)",
      description: "Wykorzystaj LinkedIn Matched Audiences do wyświetlania reklam konkretnym decision makerom. Upload list emaili/telefonów i dotieraj tylko do high-value targets.",
      howTo: [
        "Przygotuj listę target accounts + decision makerów (email/telefon/LinkedIn URL)",
        "Upload do LinkedIn Matched Audiences (minimum 300 kontaktów dla list-based)",
        "Segmentacja: tier 1 konta (premium content), tier 2/3 (broad awareness)",
        "Kreacje spersonalizowane: odniesienia do branży, pain points, case studies",
        "Multi-format strategy: Sponsored Content + InMail + Text Ads",
        "Retargeting engagement: kto zobaczył reklamę → ciepły lead dla sales",
        "Koordynacja z sales: sales widzi kto widział reklamy (LinkedIn Sales Navigator)"
      ]
    }
  ];

  const stats = [
    { label: "Czas skalowania", value: "3 miesiące", progress: 80 },
    { label: "Wymagany budżet", value: "średni", progress: 75 },
    { label: "Potencjał ROI", value: "Bardzo wysoki", progress: 95 }
  ];

  const competitiveAdvantages = [
    {
      icon: <Sparkles className="h-6 w-6 text-primary" />,
      title: "Category leadership positioning",
      description: "Zdominuj swoją niszę na LinkedIn - Twoja firma i eksperci powinni być top-of-mind dla każdego decision makera."
    },
    {
      icon: <Users2 className="h-6 w-6 text-primary" />,
      title: "Employee advocacy at scale",
      description: "20+ osób z firmy aktywnych na LinkedIn = 100x większy zasięg organiczny niż sama strona firmowa."
    },
    {
      icon: <Rocket className="h-6 w-6 text-primary" />,
      title: "Pipeline acceleration playbook",
      description: "Zmniejsz sales cycle o 30-40% dzięki pre-education i budowaniu zaufania przed pierwszym call'em."
    }
  ];
  
  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <ScrollArea className="h-screen w-full">
      <div className="min-h-screen gradient-subtle py-6 md:py-12">
        <div className="container px-3 sm:px-4">
          <div className="max-w-5xl mx-auto space-y-6 md:space-y-8 pb-12">
            {/* Domain header - always visible */}
            <Card className="gradient-hero border-none shadow-xl mb-6">
              <CardContent className="p-6 md:p-8 text-center">
                <div className="flex flex-col items-center gap-4">
                  {logoUrl ? (
                    <div className="rounded-lg bg-white p-3 shadow-lg">
                      <img 
                        src={logoUrl} 
                        alt="Company logo" 
                        className="h-12 w-auto max-w-[200px] object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                      <div className="hidden rounded-full bg-white/20 p-3">
                        <Globe className="h-8 w-8 text-white" />
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-full bg-white/20 p-3">
                      <Globe className="h-8 w-8 text-white" />
                    </div>
                  )}
                  <div>
                    {answers?.[4] && (answers[4] as string).trim() !== '' ? (
                      <>
                        <p className="text-white/90 text-lg mb-2">
                          Osiągnięty wynik dla
                        </p>
                        <h2 className="text-3xl md:text-4xl font-bold text-white">
                          {formatDomainForDisplay(answers[4] as string)}
                        </h2>
                      </>
                    ) : (
                      <h2 className="text-3xl md:text-4xl font-bold text-white">
                        Twój osiągnięty wynik
                      </h2>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Score Section - only show if score exists */}
            {score && (
              <Card className="gradient-card border-none shadow-2xl overflow-hidden">
                <CardContent className="p-0">
                  {/* Top section with gradient background */}
                  <div className="bg-gradient-to-br from-amber-500/5 via-amber-500/10 to-amber-500/5 px-6 pt-12 pb-8">
                    <div className="text-center mb-8">
                      <h2 className="text-4xl md:text-5xl font-bold mb-3 bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 bg-clip-text text-transparent">
                        Dominacja w Zasięgu
                      </h2>
                      <p className="text-lg text-muted-foreground">
                        Poziom gotowości LinkedIn
                      </p>
                    </div>
                    
                    {/* Score and Badge in elegant layout */}
                    <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
                      <div className="transform hover:scale-105 transition-transform duration-300">
                        <ScoreGauge score={score} />
                      </div>
                      <div className="transform hover:scale-105 transition-transform duration-300">
                        <LevelBadge level="high" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Bottom section with AI-generated description */}
                  <div className="px-6 py-8 bg-card">
                    <div className="max-w-3xl mx-auto">
                      {isLoadingAI ? (
                        <QuizAILoader />
                      ) : aiAnalysis ? (
                        aiAnalysis.areas ? (
                          <div className="space-y-4">
                            {aiAnalysis.areas.map((area: any, idx: number) => (
                              <AIInsightCard
                                key={idx}
                                title={area.name}
                                content={area.content}
                                icon={<Crown className="h-5 w-5 text-amber-600" />}
                                variant="highlight"
                              />
                            ))}
                            {aiAnalysis.keyInsights && aiAnalysis.keyInsights.length > 0 && (
                              <AIInsightCard
                                title="Kluczowe obserwacje"
                                content={aiAnalysis.keyInsights}
                                icon={<Brain className="h-5 w-5 text-amber-600" />}
                                variant="default"
                              />
                            )}
                          </div>
                        ) : (
                          <AIInsightCard
                            title={aiAnalysis.title || "Co oznacza Twój wynik"}
                            content={aiAnalysis.description}
                            icon={<Crown className="h-5 w-5 text-amber-600" />}
                            variant="highlight"
                          />
                        )
                      ) : (
                        <AIInsightCard
                          title="Co oznacza Twój wynik"
                          content={[
                            "Gratulacje! Twoja firma należy do 5% najlepiej przygotowanych organizacji w Polsce pod kątem LinkedIn Social Selling. Macie już wszystkie podstawowe elementy - teraz możecie skupić się na dominacji w swojej niszy.",
                            "Jesteście na etapie, gdzie drobne optymalizacje procesów i zaawansowane techniki thought leadership mogą przynieść skokowy wzrost. To moment, aby stać się niekwestionowanym liderem w swojej kategorii na LinkedIn i case study dla innych firm."
                          ]}
                          icon={<Brain className="h-5 w-5 text-amber-600" />}
                          variant="default"
                        />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            
            {/* CTA Button to open form modal */}
            <Card className="gradient-cta-red border-none shadow-xl overflow-hidden">
              <CardHeader className="text-center space-y-6 pb-8">
                <div className="flex justify-center">
                  <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm">
                    <Zap className="h-4 w-4" />
                    Zbuduj przewagę konkurencyjną na LinkedIn
                  </div>
                </div>
                
                <CardTitle className="text-2xl md:text-4xl text-white leading-tight px-4">
                  Dowiedz się, jak LinkedIn buduje kontakty, sprzedaż i reputację lidera.
                </CardTitle>
                
                <div className="max-w-2xl mx-auto space-y-4 px-4">
                  <h3 className="text-xl md:text-2xl font-semibold text-white/95">
                    Jesteś gotowy na skalowanie. Zbuduj przewagę na LinkedIn.
                  </h3>
                  
                  <p className="text-base text-white/90 leading-relaxed">
                    Twój wynik pokazuje wysoki poziom gotowości do Social Selling. Porozmawiajmy o tym, jak zwiększyć skalę działań, podnieść wartość pipeline z LinkedIn i wykorzystać obecny poziom do zbudowania przewagi nad konkurencją.
                  </p>
                </div>

                {/* Benefits list */}
                <div className="max-w-2xl mx-auto pt-4 space-y-3 text-left px-4">
                  <div className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/15 transition-colors">
                    <CheckCircle2 className="h-6 w-6 text-white shrink-0 mt-0.5" />
                    <p className="text-white/95 font-medium">Poznasz sekrety LinkedIn oraz strategie, które wykorzystują liderzy w B2B</p>
                  </div>
                  
                  <div className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/15 transition-colors">
                    <CheckCircle2 className="h-6 w-6 text-white shrink-0 mt-0.5" />
                    <p className="text-white/95 font-medium">Kierunki ekspansji i skalowania działań zespołu na LinkedIn</p>
                  </div>
                  
                  <div className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/15 transition-colors">
                    <CheckCircle2 className="h-6 w-6 text-white shrink-0 mt-0.5" />
                    <p className="text-white/95 font-medium">Wstępny zakres i poziom inwestycji dla scenariusza skalowania</p>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0 pb-8 px-4 space-y-6">
                <div className="max-w-2xl mx-auto space-y-4">
                  <Button 
                    size="lg" 
                    variant="secondary"
                    className="w-full flex items-center justify-center gap-2 text-lg py-6 h-auto bg-white text-[hsl(var(--cta-red))] hover:bg-white/90 hover:scale-105 transition-all shadow-xl"
                    onClick={() => {
                      if (typeof window !== 'undefined' && window.fbq) {
                        window.fbq('trackCustom', 'CTAClicked', {
                          cta_type: 'Scale Strategy Discussion',
                          report_band: band,
                          button_text: 'Porozmawiajmy o skalowaniu działań'
                        });
                      }
                      document.getElementById('lead-form-trigger-high')?.click();
                    }}
                  >
                    Porozmawiajmy o skalowaniu działań
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                  
                  <p className="text-sm text-white/80 text-center max-w-xl mx-auto leading-relaxed">
                    Rozmowa wsparta Twoim wynikiem i 8+ latami doświadczenia w LinkedIn. Konkretne rekomendacje dla Twojej sytuacji.
                  </p>
                </div>

                {/* Social proof section */}
                <div className="max-w-2xl mx-auto pt-6 border-t border-white/20">
                  <div className="flex items-center justify-center gap-3 text-white/90">
                    <div className="flex -space-x-2">
                      <div className="w-8 h-8 rounded-full bg-white/20 border-2 border-white flex items-center justify-center">
                        <Rocket className="h-4 w-4 text-white" />
                      </div>
                      <div className="w-8 h-8 rounded-full bg-white/20 border-2 border-white flex items-center justify-center">
                        <TrendingUp className="h-4 w-4 text-white" />
                      </div>
                      <div className="w-8 h-8 rounded-full bg-white/20 border-2 border-white flex items-center justify-center">
                        <Users className="h-4 w-4 text-white" />
                      </div>
                    </div>
                    <p className="text-sm font-medium">
                      Firmy z wysoką gotowością budują dominację w swojej kategorii na LinkedIn
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Header */}
            <div className="text-center mb-12 md:mb-16 mt-28 md:mt-40 animate-fade-in">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent px-2">
                Jak zdominować kategorię na LinkedIn
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto px-4">
                Jesteście gotowi na pełną ekspansję. Skupcie się na systematycznym skalowaniu, zaawansowanej automatyzacji i maksymalizacji ROI.
              </p>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-6 mb-6 md:mb-8">
              {stats.map((stat, index) => (
                <Card key={index} className="border-none shadow-lg bg-gradient-to-br from-primary/5 to-background">
                  <CardContent className="p-4 md:p-6">
                    <div className="text-xs md:text-sm text-muted-foreground mb-1 md:mb-2">{stat.label}</div>
                    <div className="text-xl md:text-2xl font-bold mb-2 md:mb-3 text-primary">{stat.value}</div>
                    <Progress value={stat.progress} className="h-2" />
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Main Recommendations */}
            <Card className="gradient-card border-none shadow-xl">
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-xl sm:text-2xl md:text-3xl flex items-center gap-2 md:gap-3">
                  <Crown className="h-6 w-6 md:h-8 md:w-8 text-primary flex-shrink-0" />
                  <span>Zaawansowane strategie dla liderów rynku</span>
                </CardTitle>
                <CardDescription className="text-sm md:text-base">
                  Enterprise-level podejście do LinkedIn Social Selling i Thought Leadership
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 md:space-y-8 p-4 md:p-6">
                {recommendations.map((rec, index) => (
                  <div key={index} className="group">
                    <div className="flex flex-col sm:flex-row gap-4 md:gap-6 p-4 md:p-6 rounded-xl bg-gradient-to-br from-primary/10 to-secondary/30 hover:shadow-xl transition-all border border-primary/20">
                      <div className="flex-shrink-0">{rec.icon}</div>
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                          <h3 className="font-bold text-lg md:text-xl">{rec.title}</h3>
                          <span className="text-xs md:text-sm font-semibold text-primary bg-primary/10 px-2 md:px-3 py-1 rounded-full whitespace-nowrap self-start">
                            Advanced #{index + 1}
                          </span>
                        </div>
                        <p className="text-muted-foreground mb-3 md:mb-4 text-sm md:text-base leading-relaxed">{rec.description}</p>
                        <div className="bg-background/70 rounded-lg p-3 md:p-5 space-y-2 border border-primary/10">
                          <div className="font-semibold text-xs md:text-sm mb-2 md:mb-3 flex items-center gap-2">
                            <CheckCircle2 className="h-3 w-3 md:h-4 md:w-4 text-primary flex-shrink-0" />
                            Plan wdrożenia:
                          </div>
                          <ul className="space-y-2 md:space-y-2.5">
                            {rec.howTo.map((step, i) => (
                              <li key={i} className="flex gap-2 md:gap-3 text-xs md:text-sm">
                                <span className="text-primary font-bold flex-shrink-0 mt-0.5">{i + 1}.</span>
                                <span className="text-muted-foreground leading-relaxed">{step}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Competitive Advantages */}
            <Card className="border-none shadow-xl bg-gradient-to-br from-primary/5 via-background to-background">
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-xl md:text-2xl flex items-center gap-2 md:gap-3">
                  <TrendingUp className="h-5 w-5 md:h-7 md:w-7 text-primary flex-shrink-0" />
                  <span>Przewagi konkurencyjne na poziomie eksperckim</span>
                </CardTitle>
                <CardDescription className="text-sm md:text-base">
                  Co odróżnia liderów od reszty rynku
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 md:space-y-4 p-4 md:p-6">
                {competitiveAdvantages.map((advantage, index) => (
                  <div key={index} className="flex gap-3 md:gap-4 p-4 md:p-5 rounded-lg bg-background border border-primary/20 hover:border-primary/40 transition-all hover:shadow-md">
                    <div className="flex-shrink-0 bg-primary/10 p-2 md:p-3 rounded-lg h-fit">{advantage.icon}</div>
                    <div className="flex-1">
                      <div className="font-bold mb-1 md:mb-2 text-base md:text-lg">{advantage.title}</div>
                      <div className="text-xs md:text-sm text-muted-foreground leading-relaxed">{advantage.description}</div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
            
            {/* Quick Wins */}
            <Card className="gradient-card border-none shadow-xl">
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-xl md:text-2xl flex items-center gap-2 md:gap-3">
                  <Workflow className="h-5 w-5 md:h-7 md:w-7 text-primary flex-shrink-0" />
                  <span>Inicjatywy strategiczne - Plan 90 dni</span>
                </CardTitle>
                <CardDescription className="text-sm md:text-base">
                  Działania strategiczne dla maksymalizacji ROI z LinkedIn
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 md:space-y-3 p-4 md:p-6">
                {quickWins.map((item) => (
                  <div key={item.id} className="flex items-center space-x-3 md:space-x-4 p-3 md:p-4 rounded-lg hover:bg-secondary/50 transition-all border border-secondary hover:border-primary/30">
                    <Checkbox
                      id={item.id}
                      checked={checkedItems[item.id] || false}
                      onCheckedChange={(checked) =>
                        setCheckedItems(prev => ({ ...prev, [item.id]: checked === true }))
                      }
                      className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0"
                    />
                    <label
                      htmlFor={item.id}
                      className="text-sm md:text-base font-medium leading-snug cursor-pointer flex-1"
                    >
                      {item.label}
                    </label>
                  </div>
                ))}
              </CardContent>
            </Card>
            
            {/* Download Report CTA */}
            <Card className="gradient-cta-red border-none shadow-2xl text-white overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-transparent" />
              <CardContent className="p-8 md:p-12 relative z-10">
                <div className="max-w-3xl mx-auto text-center space-y-8">
                  <div>
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">
                      Pobierz swój indywidualny raport i zalecenia
                    </h2>
                    <p className="text-lg opacity-90">
                      Zostaw swój email, a prześlemy Ci szczegółowy raport PDF z Twoimi wynikami i rekomendacjami.
                    </p>
                  </div>

                  <DownloadReportEmailForm 
                    quizSubmissionId={(location.state as any)?.quizSubmissionId} 
                    score={score}
                    band={band}
                  />
                </div>
              </CardContent>
            </Card>
            
          </div>
        </div>
      </div>
      <ReportFooter />
      
      {/* Hidden Dialog Trigger for Lead Form */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          <button id="lead-form-trigger-high" className="hidden" />
        </DialogTrigger>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Porozmawiajmy o skalowaniu działań</DialogTitle>
          </DialogHeader>
          <LeadForm
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        </DialogContent>
      </Dialog>
    </ScrollArea>
    </>
  );
};

export default ReportHigh;
